import { channelInfo } from '@op/components/ChannelLabel';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import {
  chartIconRich,
  getLegendFormat,
} from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/utils';
import { textShowFun } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import { echartsContent, getPercentageInfo } from '@op/utils';
import { getYAxisWithChannelIcon } from '@op/utils/chart';
import { dealWithOthersName } from '@op/utils/languageValue';
import { reviewsFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { chartTitle } from '../../utils';

export default function HorizontalBarChart(props: any) {
  const {
    tooltipText = 'Mentions',
    chartType,
    type,
    chartData,
    filter,
    seriesConfig,
    tooltipConfig,
    title,
    titleTips,
    rightTitle,
    rightTitleTips,
    yAxisRightConfig,
  } = props;

  const { formatMessage } = useLanguage();
  const eChartsRef = React.useRef(null as any);

  const options = useMemo(() => {
    const { dataY, total } = chartData || {};

    const series = seriesConfig?.map((val: any) => {
      const { dataKey, ...res } = val;
      let data = chartData?.[dataKey] ?? [];
      if (dataY && data.length !== dataY.length) {
        data = dataY.map((item: any) => {
          const currentItem =
            chartData?.[dataKey]?.find((val: any) => val.originName === item) ??
            {};

          return {
            name: item,
            value: currentItem.value ?? 0,
            ...currentItem,
          };
        });
      }
      return {
        ...res,
        data,
      };
    });
    const yAxis = [];
    const hasYAxisRight = yAxisRightConfig?.show || false;
    if (hasYAxisRight) {
      const rightYAxisData = yAxisRightConfig?.dataKey
        ? chartData?.[yAxisRightConfig?.rightDataKey]
        : total;
      yAxis.push({
        type: 'category',
        position: 'right',
        data: rightYAxisData,
        axisLabel: {
          formatter: (value) => `{value|${reviewsFormat(value)}}`,
          show: true,
          width: 40,
          rich: {
            value: {
              align: 'right',
            },
          },
          fontWeight: 400,
          fontSize: 12,
          lineHeight: 16,
          padding: [0, 0, 0, 0],
        },
        splitLine: {
          show: false,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        ...yAxisRightConfig,
      });
    }

    return {
      yAxis: [
        ...getYAxisWithChannelIcon({ data: dataY, formatMessage, type, corsMode: true }),
        ...yAxis,
      ],
      series,
      tooltip: {
        extraCssText: 'z-index:8',
        confine: true,
        trigger: 'axis',
        formatter: (params: any) => {
          const currentDateTotal = total[params[0].dataIndex];
          const sumText = formatMessage({ id: 'Total' });
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            textShowFun: textShowFun(formatMessage),
            beforeSort: false,
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            valueFormat: (value: number) => reviewsFormat(Math.abs(value), 2),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                if (!item.originValue) return null;
                const share = getPercentageInfo(
                  Math.abs(item.originValue),
                  total,
                  2,
                );
                return {
                  ...item,
                  value: `${item.value}(${share})`,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              newRecords.reverse();
              if (tooltipConfig?.showTotal) {
                newRecords.unshift({
                  text: sumText,
                  value: reviewsFormat(total),
                  share: '100%',
                });
              }

              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const channelIcon =
                type === 'channel'
                  ? `<img src='${
                      channelInfo(params[0]?.name.toLocaleLowerCase() as any)
                        .img
                    }'/>`
                  : '';
              const columns = [
                {
                  key: 'text',
                  header: `<span class="echart-tooltip">${channelIcon}${dealWithOthersName(
                    params[0]?.name,
                    type,
                  )}<span>`,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({
                    id: tooltipConfig?.title || 'Mentions',
                  }),
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: seriesConfig?.map((item) => ({
          name: item.name,
          itemStyle: { color: item?.color },
        })),
        formatter: getLegendFormat(formatMessage),
        textStyle: { rich: chartIconRich },
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          color: '#838697',
          fontSize: 12,
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'solid' },
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
      },
    };
  }, [chartData, formatMessage, seriesConfig, tooltipConfig, type]);

  const empty = useMemo(() => {
    return !chartData?.dataY?.length;
  }, [chartData]);

  return (
    <EChartCommon
      options={options}
      optionsProps={{ type: 'bar' }}
      ref={eChartsRef}
      empty={empty}
      {...props}
      rightTitle={chartTitle({
        formatMessage,
        title: rightTitle,
        titleTipsKey: rightTitleTips,
      })}
    />
  );
}
